import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Timeline } from 'antd';
import { useIntl } from 'react-intl';

import { useMaterialLogs } from 'utils/packaging';

import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';

import { ReactComponent as CalendarIcon } from 'assets/bosta-icons/Calendar.svg';

import './MaterialLogs.less';

const MaterialLogs = ({
  callRefreshLogs,
  setRefreshLogs,
  tierName = false,
  isPackagingPage = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    logs,
    showLoadMore,
    isLoading,
    loadMoreLogs,
    haveManyLogs,
    refreshLogs
  } = useMaterialLogs(!!isPackagingPage);
  const intl = useIntl();
  const timelineRef = useRef(null);
  const observerRef = useRef(null);
  const lastItemRef = useRef(null);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleIntersection = useCallback(
    (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting && showLoadMore && !isLoading) {
        loadMoreLogs();
      }
    },
    [showLoadMore, isLoading, loadMoreLogs]
  );

  useEffect(() => {
    if (lastItemRef.current && showLoadMore) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: timelineRef.current,
        rootMargin: '100px',
        threshold: 0.1
      });

      observerRef.current.observe(lastItemRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, showLoadMore, isLoading, logs.length]);

  useEffect(() => {
    if (callRefreshLogs) {
      refreshLogs();
      setRefreshLogs(false);
    }
  }, [callRefreshLogs]);

  return (
    <div className="br-material-logs__container">
      <div className="br-material-logs__header heading">
        <CalendarIcon />
        {intl.formatMessage({
          id: 'packaging.manage_materials.logs.title'
        })}
      </div>
      <LoadingWrapper loading={isLoading}>
        <Timeline className="br-material-logs__timeline">
          <div
            className="br-material-logs__timeline-container"
            ref={timelineRef}
          >
            {logs.map((log, index) => (
              <div
                key={index}
                ref={index === logs.length - 1 ? lastItemRef : null}
              >
                <Timeline.Item>
                  <div className="button-lg">{log.logTitle}</div>
                  <div className="text-gray">{log.takenBy}</div>

                  <div className="br-material-logs__details">
                    <div className="br-material-logs__details-info">
                      <span className="br-material-logs__details-info-title">
                        {log.logInfo}{' '}
                        {!!log?.tierName && `in tier ${log.tierName}`}
                      </span>
                      {log.description && (
                        <span className="br-shipment-details_tracking-details-date caption">
                          {intl.formatMessage({
                            id: 'packaging.manage_materials.description'
                          })}
                          {': '}
                          {log.description}
                        </span>
                      )}
                      <div className="text-gray">{log.logDate}</div>
                    </div>
                  </div>
                </Timeline.Item>
              </div>
            ))}
          </div>
        </Timeline>
      </LoadingWrapper>
    </div>
  );
};

export default MaterialLogs;
